import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useMediaQuery } from 'react-responsive'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { inject, observer } from 'mobx-react'
import styled from 'styled-components'
import Grid from '@material-ui/core/Grid'
import { Page, AUTHENTICATOR, ACCOUNT_TAB } from 'utils/constants'
import { getTranslatedTextByKey, isMobileApp } from '../utils/utils'
import { mediaQueriesBreakpoints, respondAbove, respondTo } from 'styles/mixins'
import Skeleton from 'components/Skeleton'
import { AccountSettings } from 'components/account/accountSettings/AccountSettings'
import { PastOrdersContainer } from 'components/account/pastOrders/PastOrdersContainer'
import { AccountSavedCards } from 'components/account/accountSavedCards/AccountSavedCards'
import { Authenticator } from 'components/account/authenticator/Authenticator'
import { AccountMenu } from '../components/account/accountMenu/AccountMenu'
import SavedAddresses from '../components/account/SavedAddresses'
import { useRouter } from 'next/router'
import { useStores } from 'hooks/useStores'
import oStorage from 'utils/o-storage'
import { AUTHENTICATOR_STATUS, AUTHENTICATOR_STORAGE_KEY } from 'hooks/useAuthenticator'

import type _Account from 'mobx/Account'
import type _Application from 'mobx/Application'
import type { AuthenticatorStatusKeys } from 'mobx/Account/Account.type'

const PENDING_KIOSK_QR_TOKEN_KEY = 'pendingKioskQrToken'
const QR_TOKEN_QUERY_PARAM = 'qrToken'

interface PersistedAuthenticatorState {
	status?: string
	keys?: AuthenticatorStatusKeys | null
}

// True iff this device already has a live kiosk binding persisted in `localStorage`.
// The hook mirror-writes `{status:'connected', keys, ...}` to `AUTHENTICATOR_STORAGE_KEY`
// once the kiosk completes the connect action; on the next mount it re-reads that entry
// and the polling re-confirms it (the server's `connectedKey` has no TTL, so this is
// stable across browser sessions until something explicitly disconnects).
//
// We use this as a guard at the PATCH coordination layer: an incoming `qrToken` on a
// device that's already bound is almost always a stale revisit (user re-clicked the
// same deeplink, the kiosk session has already completed) and PATCHing would just 404
// or 409, after which the failure-fallback in `useAuthenticator` calls `start() ->
// reset()` and overwrites the persisted `connected` entry with `defaultConnectionState`.
// That destroys the very state the user wanted us to honor on the next refresh. Better
// to drop the token on the floor and let the hook rehydrate.
const isAlreadyKioskConnected = (): boolean => {
	if (typeof window === 'undefined') return false
	const saved = oStorage.get(AUTHENTICATOR_STORAGE_KEY) as PersistedAuthenticatorState | null
	return saved?.status === AUTHENTICATOR_STATUS.CONNECTED && !!saved?.keys
}

const StyledGrid = styled(Grid)`
	display: flex;
	flex-direction: column-reverse;
	margin-top: 35px;

	${respondAbove.sm`
		flex-direction: row;
		column-gap: 90px;
		padding: 50px 40px 0;
	`}

	${respondTo.xl`
		column-gap: 10px;
		padding: unset;
	`}

	.fade-enter {
		opacity: 0;
	}
	.fade-enter-active {
		opacity: 1;
		transition: opacity 200ms ease-in;
	}
	.fade-exit {
		opacity: 1;
	}
	.fade-exit-active {
		opacity: 0;
		transition: opacity 200ms ease-in;
	}
`

const MenuAndLimitedOffersContainer = styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: 35px;
`

interface CurrentScreenProps {
	currentScreen: string
	setCurrentScreen: (screen: string) => void
	authenticatorQrToken?: string
	authenticatorPatchOk?: boolean
	authenticatorPatchKeys?: AuthenticatorStatusKeys
}

const CurrentScreen = ({
	currentScreen,
	setCurrentScreen,
	authenticatorQrToken,
	authenticatorPatchOk,
	authenticatorPatchKeys,
}: CurrentScreenProps) => {
	switch (currentScreen) {
		case 'accountSettings':
			return <AccountSettings />
		case 'accountMenu':
			return <AccountMenu setCurrentScreen={setCurrentScreen} />
		case 'pastOrders':
			return <PastOrdersContainer />
		case 'paymentMethods':
			return <AccountSavedCards />
		case AUTHENTICATOR:
			return (
				<Authenticator
					qrToken={authenticatorQrToken}
					patchOk={authenticatorPatchOk}
					patchKeys={authenticatorPatchKeys}
				/>
			)
		case 'savedAddresses':
			return <SavedAddresses />
		default:
			return null
	}
}

interface AccountProps {
	Account: typeof _Account
	Application: typeof _Application
}

const AccountInner = ({ Account, Application }: AccountProps) => {
	const router = useRouter()
	const isMobileView = !useMediaQuery({ query: mediaQueriesBreakpoints.lg })
	const [currentScreen, setCurrentScreen] = useState('')
	const [isDesktopOrShowingMobileMainMenu, setIsDesktopOrShowingMobileMainMenu] = useState(false)
	const { addressManagerV2Store } = useStores()

	// Kiosk-initiated QR login: the page (not the Authenticator component) owns the
	// PATCH /v3/auth/QR/:qrToken call so we can sequence it correctly around sign-in.
	const [authenticatorQrToken, setAuthenticatorQrToken] = useState<string | undefined>(undefined)
	const [authenticatorPatchOk, setAuthenticatorPatchOk] = useState<boolean | undefined>(undefined)
	const [authenticatorPatchKeys, setAuthenticatorPatchKeys] = useState<AuthenticatorStatusKeys | undefined>(undefined)
	const patchInFlight = useRef(false)
	const isMounted = useRef(true)

	useEffect(() => {
		isMounted.current = true
		return () => {
			isMounted.current = false
		}
	}, [])

	useEffect(() => {
		if (addressManagerV2Store.newLocalizationFlowEnabled && !addressManagerV2Store.savedAddressesFetched) {
			addressManagerV2Store.fetchSavedAddresses()
		}
	}, [])

	useEffect(() => {
		if (isMobileApp()) {
			if (currentScreen && currentScreen !== 'accountMenu') {
				window.history.pushState({}, '')
				window.addEventListener('popstate', () => {
					setCurrentScreen('accountMenu')
				})
			}
		}

		return () => {
			window.removeEventListener('popstate', () => {
				setCurrentScreen('accountMenu')
			})
		}
	}, [currentScreen])

	useEffect(() => {
		const isCurrentScreenIsPastOrders = router.pathname.includes('past-orders')
		const currentShownScreen = isCurrentScreenIsPastOrders ? 'pastOrders' : isMobileView ? 'accountMenu' : 'accountSettings'
		setCurrentScreen(currentShownScreen)
		setIsDesktopOrShowingMobileMainMenu(!isMobileView || (isMobileView && currentShownScreen === 'accountMenu'))
	}, [isMobileView])

	useEffect(() => {
		if (!router.query[ACCOUNT_TAB]) {
			setCurrentScreen(isMobileView ? 'accountMenu' : 'accountSettings')
		} else {
			setCurrentScreen(router.query[ACCOUNT_TAB] as string)
		}
	}, [router.asPath])

	const runPatch = useCallback(async (token: string) => {
		if (patchInFlight.current) {
			return
		}
		patchInFlight.current = true
		try {
			const result = await Account.patchQRLogin(Application.backendChannel, token)
			if (result && result.ok) {
				if (isMounted.current) {
					setAuthenticatorPatchOk(true)
					setAuthenticatorPatchKeys(result.keys)
				}
			} else {
				if (isMounted.current) setAuthenticatorPatchOk(false)
			}
		} catch {
			if (isMounted.current) setAuthenticatorPatchOk(false)
		} finally {
			patchInFlight.current = false
			try {
				sessionStorage.removeItem(PENDING_KIOSK_QR_TOKEN_KEY)
			} catch {}
			// Always strip `qrToken` from the URL after the PATCH attempt, regardless of
			// success/failure. The token is single-use the moment we sent it: a refresh on
			// a successful flow would 409 CONFLICT against the already-bound session, and a
			// refresh on a failed flow (404 expired / 401 etc.) would just keep re-firing
			// the same doomed PATCH on every remount. Either way, leaving it on the URL is
			// useless; the in-flight `authenticatorPatchOk` state is what drives the UI from
			// here on.
			const { [QR_TOKEN_QUERY_PARAM]: _omit, ...rest } = router.query
			router.replace({ pathname: router.pathname, query: rest }, undefined, { shallow: true })
		}
	}, [Account, Application.backendChannel, router])

	// Kiosk QR login wiring: detect ?qrToken=... on the authenticator tab, persist across
	// sign-in if needed, fire PATCH, then hand the result to <Authenticator>.
	//
	// Note: we deliberately do NOT register an `onSignupPopupClose` callback here even though
	// that would feel like the natural place. The page is wrapped in `<WithGuard ...>` (see
	// `Skeleton.tsx`), and `withGuard` opens the same sign-in popup on protected pages with
	// its own callback. The `Account.onSignupPopupClose` slot is single-valued and last-write-
	// wins, so `withGuard` reliably clobbers anything we register, and on `loggedIn` flipping
	// true it explicitly overwrites the slot with `() => {}` before firing it. Relying on the
	// callback here would silently lose the PATCH on every "sign in then bind" flow. Instead,
	// we fire `runPatch` from the `Account.user` effect below, which is driven by mobx and
	// can't be intercepted.
	useEffect(() => {
		const tab = router.query[ACCOUNT_TAB]
		const queryQrToken = router.query[QR_TOKEN_QUERY_PARAM]
		if (tab !== AUTHENTICATOR) {
			return
		}

		// Already-bound short-circuit: if `localStorage` shows this device is currently
		// `connected` to a kiosk, an incoming `qrToken` is a stale revisit (same deeplink
		// re-clicked, kiosk session already completed). Honoring it would 404/409 on the
		// server and our failure-fallback would call `start() -> reset()` and overwrite
		// the live `connected` entry with `defaultConnectionState`. Drop it on the floor;
		// the polling on mount will reconfirm the binding (or surface a real disconnect /
		// expiry from the server, which still goes through the right code paths).
		if (isAlreadyKioskConnected()) {
			try {
				sessionStorage.removeItem(PENDING_KIOSK_QR_TOKEN_KEY)
			} catch {}
			if (typeof queryQrToken === 'string' && queryQrToken) {
				const { [QR_TOKEN_QUERY_PARAM]: _omit, ...rest } = router.query
				router.replace({ pathname: router.pathname, query: rest }, undefined, { shallow: true })
			}
			return
		}

		// Pull either the URL param or any token we stashed before redirecting through sign-in.
		const stashed = typeof window !== 'undefined' ? sessionStorage.getItem(PENDING_KIOSK_QR_TOKEN_KEY) : null
		const token = (typeof queryQrToken === 'string' && queryQrToken) || stashed || undefined
		if (!token || patchInFlight.current) {
			return
		}

		setAuthenticatorQrToken(token)

		const isLoggedIn = Account.isAuthenticated && Account.isAuthenticated()

		if (!isLoggedIn) {
			// Persist the token so the `Account.user` effect can pick it up the moment login
			// completes. We don't open the popup ourselves either - `withGuard` already does
			// that for protected pages, and double-opening would just be one more participant
			// in the `onSignupPopupClose` race we're explicitly trying to avoid.
			try {
				sessionStorage.setItem(PENDING_KIOSK_QR_TOKEN_KEY, token)
			} catch {
				// non-fatal: sessionStorage may be unavailable in private/locked-down contexts
			}
			return
		}

		runPatch(token)
	}, [router.query[ACCOUNT_TAB], router.query[QR_TOKEN_QUERY_PARAM], runPatch])

	// Post-sign-in trigger: when the user becomes authenticated mid-flow (i.e. they landed
	// on /account?qrToken=... while logged out and then went through the sign-in popup), the
	// `onSignupPopupClose` callback is unreliable because `withGuard` overwrites it (see
	// comment on the URL effect above). Watching `Account.user` directly is reliable - the
	// page is wrapped in `observer`, and `storeUser` reassigns the `user` reference on login,
	// so this effect re-runs exactly once when authentication completes. Guards on
	// `patchInFlight` and `authenticatorPatchOk` keep us from double-firing if the URL effect
	// already drove the PATCH (the already-logged-in fast path).
	useEffect(() => {
		if (!Account.user) return
		if (router.query[ACCOUNT_TAB] !== AUTHENTICATOR) return
		if (patchInFlight.current) return
		if (authenticatorPatchOk !== undefined) return

		// Same already-bound short-circuit as the URL effect. If the user logged in on
		// a device that's already kiosk-connected, the stashed/URL token is stale and
		// PATCHing would clobber the live `connected` entry on failure-fallback.
		if (isAlreadyKioskConnected()) {
			try {
				sessionStorage.removeItem(PENDING_KIOSK_QR_TOKEN_KEY)
			} catch {}
			const queryQrToken = router.query[QR_TOKEN_QUERY_PARAM]
			if (typeof queryQrToken === 'string' && queryQrToken) {
				const { [QR_TOKEN_QUERY_PARAM]: _omit, ...rest } = router.query
				router.replace({ pathname: router.pathname, query: rest }, undefined, { shallow: true })
			}
			return
		}

		const queryQrToken = router.query[QR_TOKEN_QUERY_PARAM]
		const stashed = typeof window !== 'undefined' ? sessionStorage.getItem(PENDING_KIOSK_QR_TOKEN_KEY) : null
		const token = (typeof queryQrToken === 'string' && queryQrToken) || stashed || undefined
		if (!token) return

		setAuthenticatorQrToken(token)
		runPatch(token)
	}, [Account.user, runPatch])

	return (
		<Skeleton
			page={Page.ACCOUNT}
			showLogo={isDesktopOrShowingMobileMainMenu}
			showBack={!isDesktopOrShowingMobileMainMenu}
			headerProps={{
				customGoBack: () => {
					setCurrentScreen('accountMenu')
				},
				showCustomTitle: !isDesktopOrShowingMobileMainMenu,
				customTitle: getTranslatedTextByKey('account'),
			}}
		>
			<StyledGrid container direction="row">
				{isDesktopOrShowingMobileMainMenu && (
					<MenuAndLimitedOffersContainer>
						{!isMobileView && <AccountMenu setCurrentScreen={setCurrentScreen} currentScreen={currentScreen} />}
					</MenuAndLimitedOffersContainer>
				)}
				<SwitchTransition mode="out-in">
					<CSSTransition key={currentScreen} timeout={200} classNames="fade">
						<CurrentScreen
							currentScreen={currentScreen}
							setCurrentScreen={setCurrentScreen}
							authenticatorQrToken={authenticatorQrToken}
							authenticatorPatchOk={authenticatorPatchOk}
							authenticatorPatchKeys={authenticatorPatchKeys}
						/>
					</CSSTransition>
				</SwitchTransition>
			</StyledGrid>
		</Skeleton>
	)
}

export const Account = inject('Account', 'Application')(observer(AccountInner)) as unknown as React.FC

export default Account
